import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_BASE_URL } from '../config/api';

const API = API_BASE_URL;

/**
 * CandidateCampaignsSection — shows the candidate's outreach campaign
 * enrollments in the preview sidepanel, above the "add to campaign" list.
 * Active + waiting_task are shown prominently; completed/replied collapse below.
 * Per enrollment: campaign name, status, step-progress dots, and (when
 * waiting on a manual task) the task inline with a Complete action.
 */

const STATUS_STYLE = {
  active:       { label: 'Active',   cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  waiting_task: { label: 'Task due', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  paused:       { label: 'Paused',   cls: 'bg-gray-100 text-gray-600 border-gray-200' },
  replied:      { label: 'Replied',  cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  completed:    { label: 'Completed',cls: 'bg-gray-50 text-gray-500 border-gray-200' },
  opted_out:    { label: 'Opted out',cls: 'bg-red-50 text-red-600 border-red-200' },
};

const TYPE_LABEL = { email: 'Email', sms: 'SMS', manual_task: 'Task', wait: 'Wait', condition: 'Branch' };

function StepProgress({ steps, currentPosition }) {
  const list = Array.isArray(steps) ? steps : [];
  if (list.length === 0) return null;
  return (
    <div className="flex items-center gap-0.5 flex-1">
      {list.map((s) => {
        const isCurrent = s.position === currentPosition && !s.done;
        return (
          <div key={s.position}
            title={`Step ${s.position + 1}: ${TYPE_LABEL[s.type] || s.type}${s.done ? ' (done)' : isCurrent ? ' (current)' : ''}`}
            className={`h-1.5 flex-1 rounded-sm ${
              s.done ? 'bg-emerald-500' : isCurrent ? 'bg-amber-400' : 'bg-gray-200'
            }`} />
        );
      })}
    </div>
  );
}

export default function CandidateCampaignsSection({ candidateId, onManageCampaign }) {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDone, setShowDone] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [busyTask, setBusyTask] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const copyMessage = (taskId, text) => {
    try {
      navigator.clipboard.writeText(text || '');
      setCopiedId(taskId);
      setTimeout(() => setCopiedId(null), 1500);
    } catch (e) { /* clipboard unavailable */ }
  };

  const token = localStorage.getItem('authToken');
  const headers = useMemo(() => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    if (!candidateId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/outreach/candidate/${candidateId}/enrollments`, { headers });
      if (r.ok) setEnrollments(await r.json());
    } catch (e) { console.error('load enrollments failed', e); }
    finally { setLoading(false); }
  }, [candidateId, headers]);

  useEffect(() => { load(); }, [load]);

  const completeTask = async (taskId) => {
    setBusyTask(taskId);
    try {
      const r = await fetch(`${API}/api/outreach/tasks/${taskId}/complete`, { method: 'PUT', headers });
      if (r.ok) await load();
      else alert('Failed to complete task');
    } catch (e) { alert('Failed to complete task'); }
    finally { setBusyTask(null); }
  };

  const active = enrollments.filter(e => ['active', 'waiting_task'].includes(e.status));
  const done = enrollments.filter(e => ['paused', 'completed', 'replied', 'opted_out'].includes(e.status));

  const Row = ({ e }) => {
    const st = STATUS_STYLE[e.status] || STATUS_STYLE.active;
    return (
      <div className="border border-gray-200 rounded-lg p-2.5 space-y-1.5 bg-white">
        <div className="flex items-center justify-between gap-2">
          <button onClick={() => onManageCampaign?.(e.campaign_id)}
            className="text-xs font-semibold text-gray-800 hover:text-emerald-700 truncate text-left">
            {e.campaign_name}
          </button>
          {e.status !== 'active' && e.status !== 'waiting_task' && (
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium border flex-shrink-0 ${st.cls}`}>{st.label}</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <StepProgress steps={e.steps} currentPosition={e.current_position} />
          <span className="text-[10px] text-gray-400 flex-shrink-0 whitespace-nowrap">
            {Math.min(e.current_position + 1, e.total_steps)} / {e.total_steps}
          </span>
        </div>

        {/* Inline task when waiting */}
        {e.task && (
          <div className="mt-1 bg-amber-50 border border-amber-200 rounded p-2 space-y-1.5">
            <div className="flex items-start gap-2">
              {e.task.task_type === 'inmail' ? (
                <svg className="w-3.5 h-3.5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 110-4.13 2.06 2.06 0 010 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z"/></svg>
              ) : (
                <svg className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
              )}
              <div className="text-[11px] font-medium text-amber-900 flex-1 min-w-0">
                {e.task.title}{e.task.task_type === 'inmail' && <span className="ml-1 text-[9px] text-blue-600 font-normal">· InMail</span>}
              </div>
              <button onClick={() => completeTask(e.task.id)} disabled={busyTask === e.task.id}
                className="text-[10px] font-medium text-white bg-emerald-600 rounded px-2 py-1 hover:bg-emerald-700 disabled:opacity-50 flex-shrink-0">
                {busyTask === e.task.id ? '…' : 'Done'}
              </button>
            </div>

            {e.task.task_type === 'inmail' ? (
              <div className="space-y-1.5">
                {e.task.subject && (
                  <div className="bg-white border border-amber-100 rounded p-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[9px] font-semibold text-gray-400 uppercase">Subject</span>
                      <button onClick={() => copyMessage(`${e.task.id}-subj`, e.task.subject)}
                        className="text-[10px] font-medium text-emerald-700 hover:text-emerald-800">
                        {copiedId === `${e.task.id}-subj` ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    <div className="text-[11px] text-gray-800 mt-0.5" style={{ wordBreak: 'break-word' }}>{e.task.subject}</div>
                  </div>
                )}
                {e.task.body && (
                  <div className="bg-white border border-amber-100 rounded p-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[9px] font-semibold text-gray-400 uppercase">Message</span>
                      <button onClick={() => copyMessage(`${e.task.id}-body`, e.task.body)}
                        className="text-[10px] font-medium text-emerald-700 hover:text-emerald-800">
                        {copiedId === `${e.task.id}-body` ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    <div className="text-[10px] text-gray-700 whitespace-pre-wrap mt-0.5" style={{ wordBreak: 'break-word' }}>{e.task.body}</div>
                  </div>
                )}
                <div className="flex items-center justify-between pt-0.5">
                  <button onClick={() => copyMessage(`${e.task.id}-all`, `${e.task.subject || ''}\n\n${e.task.body || ''}`.trim())}
                    className="text-[10px] font-medium text-emerald-700 hover:text-emerald-800 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                    {copiedId === `${e.task.id}-all` ? 'Copied both' : 'Copy subject + message'}
                  </button>
                  {e.linkedin_url && (
                    <a href={e.linkedin_url} target="_blank" rel="noopener noreferrer"
                      className="text-[10px] font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 110-4.13 2.06 2.06 0 010 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z"/></svg>
                      Open LinkedIn
                    </a>
                  )}
                </div>
              </div>
            ) : (
              e.task.description && (
                <div className="bg-white border border-amber-100 rounded p-1.5">
                  <div className="text-[10px] text-gray-700 whitespace-pre-wrap" style={{ wordBreak: 'break-word' }}>{e.task.description}</div>
                  <button onClick={() => copyMessage(e.task.id, e.task.description)}
                    className="mt-1 text-[10px] font-medium text-emerald-700 hover:text-emerald-800 flex items-center gap-1">
                    {copiedId === e.task.id ? (
                      <><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>Copied</>
                    ) : (
                      <><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>Copy message</>
                    )}
                  </button>
                </div>
              )
            )}
          </div>
        )}

        {/* Next-step hint for active (non-task) sequences */}
        {e.status === 'active' && !e.task && e.current_type && (
          <div className="text-[10px] text-gray-400">
            Next: {TYPE_LABEL[e.current_type] || e.current_type}
            {e.next_step_at && ` · ${new Date(e.next_step_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}`}
          </div>
        )}
      </div>
    );
  };

  const activeCount = active.length;

  return (
    <div className="space-y-2">
      {/* Header — always shown, collapsible */}
      <button onClick={() => setCollapsed(v => !v)}
        className="w-full flex items-center gap-1.5 text-left group">
        <svg className={`w-3 h-3 text-gray-400 transition-transform ${collapsed ? '' : 'rotate-90'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Active campaigns</span>
        {activeCount > 0 && <span className="text-[10px] text-gray-400">({activeCount})</span>}
        {enrollments.some(e => e.task) && (
          <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded-full font-medium bg-amber-50 text-amber-700 border border-amber-200">Task due</span>
        )}
      </button>

      {!collapsed && (
        <div className="space-y-2">
          {loading ? (
            <div className="text-[11px] text-gray-400 px-1">Loading…</div>
          ) : (
            <>
              {activeCount === 0
                ? <div className="text-[11px] text-gray-400 px-1">Not enrolled in any active campaigns.</div>
                : active.map(e => <Row key={e.sequence_id} e={e} />)}

              {done.length > 0 && (
                <div className="pt-1">
                  <button onClick={() => setShowDone(v => !v)}
                    className="text-[10px] text-gray-400 hover:text-gray-600 flex items-center gap-1">
                    <svg className={`w-3 h-3 transition-transform ${showDone ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    {done.length} paused / completed / replied
                  </button>
                  {showDone && <div className="space-y-2 mt-2">{done.map(e => <Row key={e.sequence_id} e={e} />)}</div>}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
